import { SceneKey } from "./scene-keys";
import {
  findProtoByToken,
  type ProtoDef,
} from "./proto-registry";

export type ResolvedEntry =
  | { kind: "hub" }
  | { kind: "proto"; proto: ProtoDef };

/**
 * Resolve launch entry: URL query wins, then Vite `VITE_ENTRY`.
 * Any `PROTO_REGISTRY` id / scene / alias works — no per-proto if-chains.
 *
 * - `?entry=ffa` / `VITE_ENTRY=frontline` → that proto scene
 * - default / `hub` / `title` → Title menu
 */
export function resolveEntry(
  search = typeof window !== "undefined" ? window.location.search : "",
  envEntry = String(import.meta.env.VITE_ENTRY ?? ""),
): ResolvedEntry {
  const params = new URLSearchParams(search);
  const q =
    params.get("entry")?.toLowerCase() ||
    params.get("scene")?.toLowerCase() ||
    "";

  if (q === "hub" || q === "title" || q === "menu") {
    return { kind: "hub" };
  }
  if (q) {
    const proto = findProtoByToken(q);
    if (proto) return { kind: "proto", proto };
  }

  const env = envEntry.trim().toLowerCase();
  if (!env || env === "hub" || env === "title" || env === "menu") {
    return { kind: "hub" };
  }
  const fromEnv = findProtoByToken(env);
  if (fromEnv) return { kind: "proto", proto: fromEnv };
  return { kind: "hub" };
}

export function bootTargetScene(
  entry: ResolvedEntry = resolveEntry(),
): SceneKey {
  return entry.kind === "proto" ? entry.proto.scene : SceneKey.Title;
}

/** Direct proto share session (no Title hub). */
export function isDirectProtoEntry(
  search = typeof window !== "undefined" ? window.location.search : "",
  envEntry = String(import.meta.env.VITE_ENTRY ?? ""),
): boolean {
  return resolveEntry(search, envEntry).kind === "proto";
}

/**
 * Quit from a playable scene: restart same scene on direct entry, else Title.
 */
export function shouldRestartOnQuit(
  current: SceneKey,
  search = typeof window !== "undefined" ? window.location.search : "",
  envEntry = String(import.meta.env.VITE_ENTRY ?? ""),
): boolean {
  const entry = resolveEntry(search, envEntry);
  return entry.kind === "proto" && entry.proto.scene === current;
}

/** @deprecated use isDirectProtoEntry — kept for older call sites */
export function isFfaOnlyEntry(
  search = typeof window !== "undefined" ? window.location.search : "",
  envEntry = String(import.meta.env.VITE_ENTRY ?? ""),
): boolean {
  const entry = resolveEntry(search, envEntry);
  return entry.kind === "proto" && entry.proto.id === "ffa";
}

/** @deprecated use resolveEntry + bootTargetScene */
export type EntryMode = "hub" | "ffa";

/** @deprecated */
export function resolveEntryMode(
  search = typeof window !== "undefined" ? window.location.search : "",
  envEntry = String(import.meta.env.VITE_ENTRY ?? ""),
): EntryMode {
  const entry = resolveEntry(search, envEntry);
  if (entry.kind === "proto" && entry.proto.id === "ffa") return "ffa";
  if (entry.kind === "proto") {
    // Non-ffa direct still not "hub"; callers of EntryMode only knew ffa|hub.
    return entry.proto.id === "ffa" ? "ffa" : "hub";
  }
  return "hub";
}
